/**
 * The v7 session-only boundary.
 *
 * `packageRecommendation`, `seasonalDemand`, and `calculationReceipt` exist only
 * to help the visitor understand the estimate in front of them during the active
 * planning session. They are deliberately excluded from every persisted or
 * transmitted payload: the saved draft, the share URL, the stored shared plan,
 * the PDF request, and the submit request.
 *
 * Removing the keys outright - rather than writing their inert defaults - makes
 * the property checkable by a regression test: no serialized planner payload may
 * contain these key names at all. Reading any such payload back applies the
 * schema defaults, so an older plan or an existing share link round-trips
 * unchanged and can never acquire v7 data.
 *
 * This module is pure: no clock, no I/O, no state mutation.
 */
import {
  DEFAULT_CALCULATION_RECEIPT,
  DEFAULT_PACKAGE_RECOMMENDATION,
  DEFAULT_SEASONAL_DEMAND,
  type FunnelWizardState,
} from "@/lib/funnel-planner/schema";

export const V7_SESSION_ONLY_FIELDS = [
  "packageRecommendation",
  "seasonalDemand",
  "calculationReceipt",
] as const;

export type V7SessionOnlyField = (typeof V7_SESSION_ONLY_FIELDS)[number];

/** A planner state with every session-only field removed. */
export type PersistablePlannerState = Omit<FunnelWizardState, V7SessionOnlyField>;

/**
 * Drops the v7 session-only fields from a planner state.
 *
 * Call this at every serialization boundary. The result still satisfies
 * `FunnelWizardStateSchema` on the way back in, because each removed field
 * carries a schema default.
 */
export function toPersistablePlannerState(state: FunnelWizardState): PersistablePlannerState {
  const {
    packageRecommendation: _packageRecommendation,
    seasonalDemand: _seasonalDemand,
    calculationReceipt: _calculationReceipt,
    ...persistable
  } = state;

  return persistable;
}

/**
 * Restores the session-only keys at their inert defaults.
 *
 * Used where a downstream server type still expects a complete planner state -
 * the stored shared-plan record, for example. The result carries no session
 * data, only the same disabled defaults a fresh plan starts from, so nothing a
 * visitor did in their session is written out.
 */
export function withInertSessionFields(state: PersistablePlannerState): FunnelWizardState {
  return {
    ...state,
    packageRecommendation: { ...DEFAULT_PACKAGE_RECOMMENDATION },
    seasonalDemand: { ...DEFAULT_SEASONAL_DEMAND },
    calculationReceipt: DEFAULT_CALCULATION_RECEIPT,
  };
}

/**
 * Reports which session-only field names appear anywhere in a serialized
 * payload. Used by the regression tests that guard the boundary; an empty array
 * is the only acceptable result for anything leaving the session.
 */
export function findSessionOnlyFieldLeaks(serialized: string): readonly V7SessionOnlyField[] {
  return V7_SESSION_ONLY_FIELDS.filter((field) => serialized.includes(field));
}
